/**
 * Safety Test Models (Admin)
 *
 * Interfaces for safety/knowledge tests management in admin portal.
 */

// ============================================
// TEST STATUS
// ============================================

export type AdminTestStatus = 'draft' | 'active' | 'retired';

// ============================================
// SAFETY TEST CATEGORY
// ============================================

export interface SafetyTestCategory {
  id: string;
  name: string;
  shortName: string;
  description?: string;
  icon: string;
  isRequired: boolean;
  frequency: 'annual' | 'biannual' | 'once' | 'custom';
  frequencyDays?: number;
  sortOrder: number;
  organizationId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SafetyTestCategoryCreateRequest {
  name: string;
  shortName?: string;
  description?: string;
  icon?: string;
  isRequired?: boolean;
  frequency?: 'annual' | 'biannual' | 'once' | 'custom';
  frequencyDays?: number;
  sortOrder?: number;
  organizationId?: string;
  isSystem?: boolean;
}

export interface SafetyTestCategoryUpdateRequest {
  name?: string;
  shortName?: string;
  description?: string;
  icon?: string;
  isRequired?: boolean;
  frequency?: 'annual' | 'biannual' | 'once' | 'custom';
  frequencyDays?: number;
  sortOrder?: number;
}

// ============================================
// SAFETY TEST
// ============================================

export interface SafetyTest {
  id: string;
  categoryId?: string;
  category?: SafetyTestCategory;
  organizationId?: string;
  title: string;
  shortName?: string;
  description?: string;
  icon?: string;
  status: AdminTestStatus;
  passingScore: number;
  timeLimit?: number;
  version: number;
  versionNotes?: string;
  effectiveDate?: string;
  parentTestId?: string;
  shuffleQuestions: boolean;
  shuffleOptions: boolean;
  studyGuideUrl?: string;
  studyGuideFilename?: string;
  questionCount?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface SafetyTestWithQuestions extends SafetyTest {
  /**
   * Optional because the API can return a SafetyTestWithQuestions row
   * with `questions` missing/null on partial loads — the
   * safety-test-detail template guards every access on it
   * (`*ngIf="test.questions && test.questions.length > 0"`, etc.).
   * Making this optional matches reality and lets Angular's template
   * type-narrowing keep the safe-access warnings honest.
   */
  questions?: TestQuestion[];
}

export interface SafetyTestCreateRequest {
  title: string;
  shortName?: string;
  description?: string;
  icon?: string;
  categoryId?: string;
  organizationId?: string;
  passingScore?: number;
  timeLimit?: number;
  shuffleQuestions?: boolean;
  shuffleOptions?: boolean;
  versionNotes?: string;
}

export interface SafetyTestUpdateRequest {
  title?: string;
  shortName?: string;
  description?: string;
  icon?: string;
  categoryId?: string;
  passingScore?: number;
  timeLimit?: number;
  shuffleQuestions?: boolean;
  shuffleOptions?: boolean;
  versionNotes?: string;
}

// ============================================
// TEST QUESTION
// ============================================

export interface TestQuestion {
  id: string;
  testId: string;
  text: string;
  explanation?: string;
  type: 'multiple_choice' | 'true_false';
  sortOrder: number;
  options: TestQuestionOption[];
  createdAt?: string;
  updatedAt?: string;
}

export interface TestQuestionOption {
  id: string;
  questionId: string;
  text: string;
  isCorrect: boolean;
  sortOrder: number;
}

export interface TestQuestionCreateRequest {
  text: string;
  explanation?: string;
  type?: 'multiple_choice' | 'true_false';
  options: { text: string; isCorrect: boolean }[];
}

export interface TestQuestionUpdateRequest {
  text?: string;
  explanation?: string;
  type?: 'multiple_choice' | 'true_false';
}

// ============================================
// TEST VERSION HISTORY
// ============================================

export interface TestVersionHistory {
  id: string;
  version: number;
  status: AdminTestStatus;
  versionNotes?: string;
  effectiveDate?: string;
  createdAt: string;
  createdBy?: string;
}

// ============================================
// TEST ATTEMPT (Admin View)
// ============================================

export interface TestAttempt {
  id: string;
  testId: string;
  testTitle?: string;
  profileId: string;
  profileName?: string;
  profileEmail?: string;
  organizationId: string;
  organizationName?: string;
  status: 'in_progress' | 'completed' | 'timed_out' | 'abandoned';
  score?: number;
  passed?: boolean;
  totalQuestions: number;
  correctAnswers?: number;
  startedAt: string;
  completedAt?: string;
  timeSpentSeconds?: number;
  expiresAt?: string;
}

// ============================================
// ANALYTICS TYPES
// ============================================

export interface TestAnalyticsSummary {
  totalTests: number;
  totalAttempts: number;
  overallPassRate: number;
  averageScore: number;
}

export interface TestMetrics {
  testId: string;
  testTitle: string;
  attempts: number;
  passRate: number;
  averageScore: number;
  averageTimeSeconds: number;
}

export interface QuestionDifficulty {
  questionId: string;
  testId: string;
  testTitle: string;
  questionText: string;
  totalAttempts: number;
  incorrectCount: number;
  failureRate: number;
}

export interface TestAnalytics {
  summary: TestAnalyticsSummary;
  testMetrics: TestMetrics[];
  questionDifficulty: QuestionDifficulty[];
  trendData?: {
    date: string;
    passRate: number;
    attempts: number;
  }[];
}
